/**
 * SEO tag generation utilities for images across RentAnythingStore.
 *
 * Each builder returns `{ fileName, altText, titleTag, caption }` where every
 * field is capped at 125 characters and `fileName` is a lowercase-hyphenated
 * slug with a `.jpg` extension.
 */

const MAX_LEN = 125;
const MAX_SLUG_LEN = 80;

const slugify = parts =>
  parts
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, MAX_SLUG_LEN);

const cap = (str, limit = MAX_LEN) => String(str).slice(0, limit);

const squish = str => str.replace(/\s+/g, ' ').trim();

/**
 * Build SEO tags for a listing image.
 *
 * @param {Object} listing - Sharetribe listing object
 * @param {number} [index=0] - 0-based index of the image in the listing's image array
 * @param {string} [storeName=''] - The display name of the store that owns the listing
 * @returns {{ fileName: string, altText: string, titleTag: string, caption: string }}
 */
export const buildListingImageSeoTags = (listing, index = 0, storeName = '') => {
  const title = listing?.attributes?.title || '';
  const publicData = listing?.attributes?.publicData || {};
  const condition = publicData.condition || '';
  const city = publicData.city || publicData.location?.city || '';

  const slug = slugify([condition, title, 'rental', city]);

  if (index === 0) {
    const byLine = storeName ? `at ${storeName}` : 'on RentAnythingStore';
    const altParts = [condition, title, 'for rent', city && `in ${city}`];
    const titleParts = ['Rent', condition, title, city && `in ${city}`, '– RentAnythingStore'];
    const captionParts = [title, 'available for rental', city && `in ${city}`, byLine];

    return {
      fileName: cap(`${slug}.jpg`),
      altText: cap(squish(altParts.filter(Boolean).join(' '))),
      titleTag: cap(squish(titleParts.filter(Boolean).join(' '))),
      caption: cap(squish(captionParts.filter(Boolean).join(' ')), 200),
    };
  }

  const n = index + 1;
  const byLine = storeName ? `at ${storeName}` : 'on RentAnythingStore';
  const altParts = [title, `– photo ${n}`, city && `in ${city}`];
  const titleParts = [title, `rental image ${n}`, city && `in ${city}`];
  const captionParts = [title, 'available for rent', city && `in ${city}`, byLine];

  return {
    fileName: cap(`${slug}-${n}.jpg`),
    altText: cap(squish(altParts.filter(Boolean).join(' '))),
    titleTag: cap(squish(titleParts.filter(Boolean).join(' '))),
    caption: cap(squish(captionParts.filter(Boolean).join(' ')), 200),
  };
};

/**
 * Build SEO tags for a listing instruction/demo video.
 *
 * Unlike image builders, this does NOT return a `fileName` field because video
 * assets are identified by their Cloudinary `public_id`, not a filename slug.
 *
 * @param {Object} listing - Sharetribe listing object
 * @param {string} [storeName=''] - The display name of the store that owns the listing
 * @returns {{ altText: string, titleTag: string, caption: string }}
 */
export const buildListingVideoSeoTags = (listing, storeName = '') => {
  const title = listing?.attributes?.title || '';
  const publicData = listing?.attributes?.publicData || {};
  const city = publicData.city || publicData.location?.city || '';

  const altParts = [title, 'instruction video', city && `in ${city}`];
  const titleParts = ['How to use', title, city && `in ${city}`, '– RentAnythingStore'];
  const byLine = storeName ? `at ${storeName}` : 'on RentAnythingStore';
  const captionParts = ['Watch how to use the', title, 'rental', byLine];

  return {
    altText: cap(squish(altParts.filter(Boolean).join(' '))),
    titleTag: cap(squish(titleParts.filter(Boolean).join(' '))),
    caption: cap(squish(captionParts.filter(Boolean).join(' '))),
  };
};

/**
 * Build SEO tags for a store profile (logo/avatar) image.
 *
 * @param {string} [storeName=''] - The display name of the store
 * @param {string} [city=''] - The city where the store is based
 * @returns {{ fileName: string, altText: string, titleTag: string, caption: string }}
 */
export const buildStoreProfileSeoTags = (storeName = '', city = '') => {
  const slug = slugify([storeName, 'rental-store', city]);

  const altParts = [storeName, 'rental store', city && `in ${city}`];
  const titleParts = [storeName, '– Rental Store', city && `in ${city}`, 'on RentAnythingStore'];
  const captionParts = [
    storeName,
    'is a rental store on RentAnythingStore',
    city && `in ${city}`,
  ];

  return {
    fileName: cap(`${slug}-logo.jpg`),
    altText: cap(squish(altParts.filter(Boolean).join(' '))),
    titleTag: cap(squish(titleParts.filter(Boolean).join(' '))),
    caption: cap(squish(captionParts.filter(Boolean).join(' '))),
  };
};

/**
 * Build SEO tags for a store banner/cover image.
 *
 * @param {string} [storeName=''] - The display name of the store
 * @param {string} [city=''] - The city where the store is based
 * @returns {{ fileName: string, altText: string, titleTag: string, caption: string }}
 */
export const buildStoreBannerSeoTags = (storeName = '', city = '') => {
  const slug = slugify([storeName, 'store-banner', city]);

  const altParts = [storeName, 'store banner', city && `in ${city}`];
  const titleParts = [storeName, 'rental store banner', city && `in ${city}`];
  const captionParts = [
    'Browse and rent items from',
    storeName,
    city && `in ${city}`,
    'on RentAnythingStore',
  ];

  return {
    fileName: cap(`${slug}.jpg`),
    altText: cap(squish(altParts.filter(Boolean).join(' '))),
    titleTag: cap(squish(titleParts.filter(Boolean).join(' '))),
    caption: cap(squish(captionParts.filter(Boolean).join(' '))),
  };
};

/**
 * Build SEO tags for a store introduction video.
 *
 * @param {string} [storeName=''] - The display name of the store
 * @param {string} [city=''] - The city where the store is based
 * @param {string} [videoTitle=''] - Owner-authored video title
 * @param {string} [videoDescription=''] - Owner-authored video description
 * @returns {{ altText: string, titleTag: string, caption: string }}
 */
export const buildStoreVideoSeoTags = (storeName = '', city = '', videoTitle = '', videoDescription = '') => {
  const cityStr = city ? ` in ${city}` : '';

  const altText = videoTitle
    ? `${videoTitle} – ${storeName} rental store video`
    : `${storeName} store introduction video${cityStr}`;

  const titleTag = videoTitle
    ? `${videoTitle} – ${storeName}`
    : `${storeName} rental store video${cityStr}`;

  const caption = videoDescription
    ? videoDescription
    : `Watch ${storeName}'s store introduction video${cityStr} on RentAnythingStore`;

  return {
    altText: cap(squish(altText)),
    titleTag: cap(squish(titleTag)),
    caption: cap(squish(caption)),
  };
};
